import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FeedbackComponent } from 'src/app/general-components/feedback/feedback.component';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { Subscription } from 'rxjs';
import { DownloadEvent, DownloadJobPhase, DownloadJobStatus, DownloadJobView } from './download-job.model';
import { DownloadEventsService } from './download-events.service';
import { ProductDownloadsService } from './product-downloads.service';

@Component({
  selector: 'app-product-downloads',
  templateUrl: './product-downloads.component.html',
  styleUrls: ['./product-downloads.component.scss'],
})
export class ProductDownloadsComponent implements OnInit, OnDestroy {
  @ViewChild(FeedbackComponent) feedbackComponent!: FeedbackComponent;

  readonly DownloadJobStatus = DownloadJobStatus;

  downloads: DownloadJobView[] = [];
  generatingPricat = false;
  generatingBmecat = false;

  feedbackSettings: FeedbackSettings = {
    identifier: '',
    show: false,
    type: FeedbackType.error,
    message: '',
    closeButton: false,
  };

  private subscriptions = new Subscription();
  private expandedJobIds = new Set<string>();

  constructor(
    private downloadsService: ProductDownloadsService,
    private eventsService: DownloadEventsService
  ) {}

  ngOnInit(): void {
    this.loadDownloads();
    this.connectWebSocket();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.eventsService.disconnect();
  }

  generatePricat(): void {
    this.eventsService.reconnect();
    this.generatingPricat = true;
    this.feedbackSettings.show = false;

    this.subscriptions.add(
      this.downloadsService.generateDownload('pricat').subscribe({
        next: response => {
          this.generatingPricat = false;
          this.upsertJob(response.job);
          if (!response.started && response.job.warning) {
            this.feedbackComponent.showFeedback('', FeedbackType.success, response.job.warning, undefined, true);
          }
        },
        error: () => {
          this.generatingPricat = false;
          this.feedbackComponent.showFeedback(
            '',
            FeedbackType.error,
            'Er is iets misgegaan bij het starten van de Pricat generatie.',
            undefined,
            true
          );
        },
      })
    );
  }

  generateBmecat(): void {
    this.eventsService.reconnect();
    this.generatingBmecat = true;
    this.feedbackSettings.show = false;

    this.subscriptions.add(
      this.downloadsService.generateDownload('bmecat').subscribe({
        next: response => {
          this.generatingBmecat = false;
          this.upsertJob(response.job);
          if (!response.started && response.job.warning) {
            this.feedbackComponent.showFeedback('', FeedbackType.success, response.job.warning, undefined, true);
          }
        },
        error: () => {
          this.generatingBmecat = false;
          this.feedbackComponent.showFeedback(
            '',
            FeedbackType.error,
            'Er is iets misgegaan bij het starten van de BMeCat generatie.',
            undefined,
            true
          );
        },
      })
    );
  }

  downloadFile(job: DownloadJobView): void {
    this.subscriptions.add(
      this.downloadsService.downloadFile(job.downloadType, job.jobId).subscribe({
        next: response => {
          this.triggerDownload(response, job.filename, job.downloadType);
        },
        error: () => {
          this.feedbackComponent.showFeedback(
            '',
            FeedbackType.error,
            'Er is iets misgegaan bij het downloaden van het bestand.',
            undefined,
            true
          );
        },
      })
    );
  }

  getProgressPercent(job: DownloadJobView): number {
    // Phase weights reflect typical time distribution:
    // COUNTING_DB ~14s, QUERYING_DB+MAPPING_RECORDS ~21s concurrent (first ~18s idle waiting for
    // first DB rows, then ~3s of rapid batching), WRITING_PRICAT ~1s, WRITING_ZIP <0.1s
    // QUERYING_DB and MAPPING_RECORDS interleave 1 ms apart with the same processedRecords, so
    // both share the same base (40%). QUERYING_DB gets zero range and MAPPING_RECORDS drives the
    // bar from 40→96%. The 1 ms interleave means QUERYING_DB "flashes" never land in a paint frame.
    // WRITING_WORKBOOK is the BMeCat equivalent of WRITING_PRICAT and shares the same weight (96%).
    const phases: [DownloadJobPhase, number][] = [
      [DownloadJobPhase.STARTING, 0],
      [DownloadJobPhase.COUNTING_DB, 2],
      [DownloadJobPhase.QUERYING_DB, 40],
      [DownloadJobPhase.MAPPING_RECORDS, 40],
      [DownloadJobPhase.WRITING_PRICAT, 96],
      [DownloadJobPhase.WRITING_WORKBOOK, 96],
      [DownloadJobPhase.WRITING_ZIP, 98],
      [DownloadJobPhase.READY, 100],
    ];
    const phaseBase = new Map(phases);
    const base = phaseBase.get(job.phase) ?? 0;
    const phaseIndex = phases.findIndex(([p]) => p === job.phase);
    const ceiling = phaseIndex >= 0 && phaseIndex + 1 < phases.length ? phases[phaseIndex + 1][1] : base;

    if (job.processedRecords != null && job.totalRecords != null && job.totalRecords > 0) {
      return Math.round(base + (job.processedRecords / job.totalRecords) * (ceiling - base));
    }
    return base;
  }

  isIndeterminate(job: DownloadJobView): boolean {
    return (
      (job.status === DownloadJobStatus.QUEUED || job.status === DownloadJobStatus.RUNNING) &&
      (job.phase === DownloadJobPhase.STARTING ||
        job.phase === DownloadJobPhase.COUNTING_DB ||
        job.phase === DownloadJobPhase.WRITING_PRICAT ||
        job.phase === DownloadJobPhase.WRITING_WORKBOOK ||
        job.phase === DownloadJobPhase.WRITING_ZIP)
    );
  }

  getStatusLabel(job: DownloadJobView): string {
    switch (job.status) {
      case DownloadJobStatus.QUEUED:
        return 'Wachtrij';
      case DownloadJobStatus.RUNNING:
        return job.statusMessage ?? 'Bezig';
      case DownloadJobStatus.READY:
        return 'Gereed';
      case DownloadJobStatus.FAILED:
        return 'Mislukt';
      case DownloadJobStatus.DELETING:
        return 'Verwijderen';
      default:
        return job.status;
    }
  }

  /**
   * A completed-generation summary is available (only READY jobs that produced statistics carry one).
   * Adjustments alone never make a job look problematic; only rejected rows do.
   */
  hasSummary(job: DownloadJobView): boolean {
    return job.rejectedRows != null;
  }

  /** The export is complete: every source row made it into the file. */
  isComplete(job: DownloadJobView): boolean {
    return this.hasSummary(job) && (job.rejectedRows ?? 0) === 0;
  }

  /** There is something worth expanding: excluded rows and/or automatic adjustments. */
  hasDetails(job: DownloadJobView): boolean {
    return this.hasSummary(job) && ((job.rejectedRows ?? 0) > 0 || (job.adjustmentCount ?? 0) > 0);
  }

  resultBadgeLabel(job: DownloadJobView): string {
    return this.isComplete(job) ? 'Volledig' : `${job.rejectedRows} overgeslagen`;
  }

  completenessText(job: DownloadJobView): string {
    return `${this.formatCount(job.writtenRows)} van ${this.formatCount(job.totalRows)} regels geëxporteerd`;
  }

  isExpanded(job: DownloadJobView): boolean {
    return this.expandedJobIds.has(job.jobId);
  }

  toggleExpanded(job: DownloadJobView): void {
    if (this.expandedJobIds.has(job.jobId)) {
      this.expandedJobIds.delete(job.jobId);
    } else {
      this.expandedJobIds.add(job.jobId);
    }
  }

  private formatCount(value: number | null | undefined): string {
    return value == null ? '-' : value.toLocaleString('nl-NL');
  }

  formatFileSize(bytes: number | null): string {
    if (bytes == null) {
      return '-';
    }
    if (bytes < 1024) {
      return String(bytes) + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  private loadDownloads(): void {
    this.subscriptions.add(
      this.downloadsService.getAllDownloads().subscribe({
        next: response => (this.downloads = response.items),
        error: () => {
          this.feedbackComponent.showFeedback(
            '',
            FeedbackType.error,
            'Kan de downloadlijst niet ophalen.',
            undefined,
            true
          );
        },
      })
    );
  }

  private connectWebSocket(): void {
    this.eventsService.connect();
    this.subscriptions.add(
      this.eventsService.events$.subscribe(event => {
        this.handleEvent(event);
      })
    );
  }

  private handleEvent(event: DownloadEvent): void {
    if (event.eventType === 'job-pruned') {
      this.downloads = this.downloads.filter(j => j.jobId !== event.jobId);
    } else {
      this.upsertJob(event);
    }
  }

  private upsertJob(job: DownloadJobView): void {
    const index = this.downloads.findIndex(j => j.jobId === job.jobId);
    if (index >= 0) {
      this.downloads[index] = job;
    } else {
      this.downloads = [job, ...this.downloads];
    }
  }

  private triggerDownload(
    response: { body: Blob | null; headers: { get(name: string): string | null } },
    fallbackFilename: string | null,
    downloadType: string
  ): void {
    if (!response.body) {
      return;
    }

    const blob = new Blob([response.body], { type: 'application/zip' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="?([^";\s]+)"?/);
    const formattedDate = new Date().toISOString().split('T')[0];
    link.download = filenameMatch?.[1] ?? fallbackFilename ?? `${downloadType}-${formattedDate}.zip`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }
}
